import { useEffect, useState } from 'react';
import { billingApi } from '../services/billingApi';
import type { BillingSource } from '../types/billing';

/**
 * Fetches /api/me/allowed-sources once on mount and exposes the result.
 *
 * Empty array means "no filter" - admins and not-yet-provisioned users see
 * everything the backend returns. A single-source array means the Upload
 * radio should lock to that source. A two-source array means both radios
 * stay enabled.
 *
 * Filtering is advisory in the SPA - the backend already restricts results
 * to the caller's allowed sources. These UX nudges keep operators from
 * picking the wrong source by accident.
 */
export function useAllowedSources(): {
  allowedSources: BillingSource[] | null;
  loading: boolean;
  error?: Error;
} {
  const [allowedSources, setAllowedSources] = useState<BillingSource[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    billingApi
      .getAllowedSources()
      .then((r) => {
        if (!cancelled) setAllowedSources(r.allowedSources);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { allowedSources, loading, error };
}
